import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { ageGroupsAPI } from '../services/api';
import { AgeGroup } from '../types/ageGroup';
import AgeGroupModal from '../components/ageGroups/AgeGroupModal';
import AgeGroupDetail from '../components/ageGroups/AgeGroupDetail';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';
import FilterAccordion from '../components/ui/FilterAccordion';
import SlideOver from '../components/ui/SlideOver';
import { PlusIcon, PencilIcon, TrashIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const AgeGroups: React.FC = () => {
  usePageTitle('Korosztályok');
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<AgeGroup | null>(null);
  const [detailAgeGroup, setDetailAgeGroup] = useState<AgeGroup | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ageGroupToDelete, setAgeGroupToDelete] = useState<AgeGroup | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);

  useEffect(() => {
    loadAgeGroups();
  }, []);

  const loadAgeGroups = async () => {
    try {
      setLoading(true);
      const data = await ageGroupsAPI.getAll();
      setAgeGroups(data);
    } catch (error) {
      console.error('Failed to load age groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedAgeGroup(null);
    setIsModalOpen(true);
  };

  const handleEdit = (ageGroup: AgeGroup) => {
    setSelectedAgeGroup(ageGroup);
    setIsModalOpen(true);
  };

  const handleDelete = (ageGroup: AgeGroup) => {
    setAgeGroupToDelete(ageGroup);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (ageGroupToDelete) {
      try {
        await ageGroupsAPI.delete(ageGroupToDelete.id);
        loadAgeGroups();
        if (detailAgeGroup?.id === ageGroupToDelete.id) {
          setDetailAgeGroup(null);
        }
      } catch (error) {
        console.error('Failed to delete age group:', error);
      }
    }
    setDeleteConfirmOpen(false);
    setAgeGroupToDelete(null);
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    loadAgeGroups();
  };

  const openDetail = (ageGroup: AgeGroup) => {
    setDetailAgeGroup(ageGroup);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  // Filter age groups based on showEmpty setting
  const filteredAgeGroups = showEmpty
    ? ageGroups
    : ageGroups.filter(ag => ag.playerCount > 0 || ag.coachCount > 0);

  const emptyCount = ageGroups.filter(ag => ag.playerCount === 0 && ag.coachCount === 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Betöltés...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Korosztályok</h1>
        <button
          onClick={handleCreate}
          className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Új korosztály
        </button>
      </div>

      {/* Filter checkbox */}
      {emptyCount > 0 && (
        <div className="mb-4">
          <FilterAccordion activeCount={showEmpty ? 1 : 0}>
            <label className="inline-flex items-center cursor-pointer py-1">
              <input
                type="checkbox"
                checked={showEmpty}
                onChange={(e) => setShowEmpty(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                Üres korosztályok megjelenítése ({emptyCount})
              </span>
            </label>
          </FilterAccordion>
        </div>
      )}

      {filteredAgeGroups.length === 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="text-center py-12">
            <p className="text-gray-500">
              {ageGroups.length === 0 ? 'Még nincsenek korosztályok.' : 'Nincs megjeleníthető korosztály.'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Mobil kártyák (md alatt) */}
          <div className="md:hidden grid grid-cols-2 gap-3">
            {filteredAgeGroups.map((ageGroup) => (
              <React.Fragment key={ageGroup.id}>
                <div
                  className={`rounded-lg shadow border p-3 flex flex-col cursor-pointer transition-colors ${
                    !ageGroup.isActive ? 'bg-gray-100 opacity-60' : 'bg-white hover:bg-gray-50'
                  }`}
                  onClick={() => openDetail(ageGroup)}
                >
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <h3 className="min-w-0 font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug">
                      {ageGroup.name}
                      {!ageGroup.isActive && (
                        <span className="ml-1 text-xs text-gray-500">(inaktív)</span>
                      )}
                    </h3>
                    <span className="shrink-0">
                      <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1.5">
                    Születési dátum: {formatDate(ageGroup.birthDateFrom)} - {formatDate(ageGroup.birthDateTo)}
                  </p>
                  <div className="flex-1 flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-lg font-semibold text-gray-900 leading-tight">{ageGroup.playerCount}</p>
                      <p className="text-xs text-gray-500">játékos</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-gray-900 leading-tight">{ageGroup.coachCount}</p>
                      <p className="text-xs text-gray-500">edző</p>
                    </div>
                  </div>
                  <div
                    className="mt-2 flex justify-end gap-1 border-t border-gray-100 pt-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleEdit(ageGroup)}
                      className="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                      title="Szerkesztés"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(ageGroup)}
                      className="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50"
                      title="Törlés"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Desktop lista (md-től) */}
          <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="divide-y divide-gray-200">
            {filteredAgeGroups.map((ageGroup) => (
              <div key={ageGroup.id}>
                <div
                  className={`px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !ageGroup.isActive ? 'bg-gray-100 opacity-60' : ''
                  }`}
                  onClick={() => openDetail(ageGroup)}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg font-medium text-gray-900">
                          {ageGroup.name}
                          {!ageGroup.isActive && (
                            <span className="ml-2 text-xs text-gray-500">(inaktív)</span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Születési dátum: {formatDate(ageGroup.birthDateFrom)} - {formatDate(ageGroup.birthDateTo)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pl-9 sm:pl-0 sm:justify-end sm:space-x-6">
                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <p className="text-2xl font-semibold text-gray-900">{ageGroup.playerCount}</p>
                          <p className="text-xs text-gray-500">játékos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-semibold text-gray-900">{ageGroup.coachCount}</p>
                          <p className="text-xs text-gray-500">edző</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEdit(ageGroup)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                          title="Szerkesztés"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(ageGroup)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50"
                          title="Törlés"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        </>
      )}

      {/* Részletpanel: jobbról beúszó, mobilon teljes képernyős */}
      <SlideOver
        isOpen={!!detailAgeGroup}
        onClose={() => setDetailAgeGroup(null)}
        title={detailAgeGroup?.name || ''}
        maxWidthClass="sm:max-w-2xl lg:max-w-4xl"
      >
        {detailAgeGroup && (
          <AgeGroupDetail
            ageGroupId={detailAgeGroup.id}
            ageGroupName={detailAgeGroup.name}
            birthDateFrom={detailAgeGroup.birthDateFrom}
            birthDateTo={detailAgeGroup.birthDateTo}
            onUpdate={loadAgeGroups}
          />
        )}
      </SlideOver>

      <AgeGroupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
        ageGroup={selectedAgeGroup}
      />

      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Korosztály törlése"
        message={`Biztosan törölni szeretnéd? Ez a művelet nem vonható vissza.`}
        itemName={ageGroupToDelete?.name || ''}
      />
    </div>
  );
};

export default AgeGroups;
